import React, { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';

import MaxTable from './MaxTable';
import MaxChart from './MaxChart';
import normalizeMaxValForSort from '../../../../shared/utils/normalizeMaxValForSort';

// Helper: Normalize name values for sorting (case-insensitive, handle empty values)
const normalizeNameValue = (value) => {
  if (!value || value === '--' || value === '') {
    return null;
  }
  return String(value).toLowerCase().trim();
};

// Helper: Compare two values, handling nulls (nulls sort to end)
const compareValues = (a, b) => {
  if (a === null && b === null) return 0;
  if (a === null) return 1; // a sorts after b
  if (b === null) return -1; // b sorts after a

  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

const TableContainer = styled('div')`
  overflow: scroll;
  display: flex;
  flex-direction: column;
  padding-bottom: 30px;

  @media screen and (max-height: 825px) {
    overflow: visible;
  }
`;

const ChartContainer = styled('div')`
  margin: 0px 30px;
`;

const ReportContainer = styled('div')`
  display: flex;
  flex-direction: column;
  overflow: visible;
  flex-grow: 1;
  min-height: 0;
  overflow: auto;
`;

const MaxReportData = () => {
  const [activeAthlete, setActiveAthlete] = useState(0);
  const [athletesChartData, setAthletesChartData] = useState({});

  const average = useSelector((state) => state.reporting.data.maxReport.values.average);
  const maxReport = useSelector((state) => state.reporting.data.maxReport.data);
  const layoutHeight = useSelector((state) => state.reporting.ui.layoutHeight);

  // Using useCallback to memoize the tableColumns and tableData array so it only forces the table
  // component to re-render if maxReport changes. In this case the activeAthlete state for the chart
  // was rendering the table for no reason.

  // Table Columns Prep
  const tableColumns = useCallback(maxReport?.columns.map((column) => ({
    name: column.valueDescription,
    selector: column.columnId,
    sortable: true,
    sortFunction: (rowA, rowB) => {
      // Average row always sorts first, regardless of column or sort direction
      const isRowAAverage = rowA.userFirst === 'Average';
      const isRowBAverage = rowB.userFirst === 'Average';

      if (isRowAAverage && isRowBAverage) {
        return 0; // Both are Average (shouldn't happen, but handle it)
      }
      if (isRowAAverage) {
        return -1; // Average (rowA) sorts before rowB
      }
      if (isRowBAverage) {
        return 1; // Average (rowB) sorts before rowA (return 1 means rowA > rowB, so rowB first)
      }

      const a = rowA[column.columnId];
      const b = rowB[column.columnId];

      // Name columns: case-insensitive string sorting
      if (column.columnId === 'userFirst' || column.columnId === 'userLast') {
        return compareValues(normalizeNameValue(a), normalizeNameValue(b));
      }

      // Numeric/time columns: normalize then compare
      const normalizedMaxValA = normalizeMaxValForSort(a);
      const normalizedMaxValB = normalizeMaxValForSort(b);
      return compareValues(normalizedMaxValA, normalizedMaxValB);
    },
    style: {
      fontWeight: column.columnId === 'userFirst' || column.columnId === 'userLast' ? '700' : '400',
    },
    center: !((column.columnId === 'userFirst' || column.columnId === 'userLast')),
    omit: column.columnId === 'userId',
  })), [maxReport]);

  // Table Data Prep
  // const tableData = useCallback([], [maxReport]); Caused issues with duplicate rows when sorting.
  const tableData = [];
  maxReport?.rows.forEach((row) => {
    // Don't add the avearge row to the table if show averages is toggled off
    if (!average && row[0].columnId === 'average') {
      return;
    }
    const tableRow = {};
    row.forEach((column) => {
      const { columnId } = column;
      // Create a key value based on the columnId and displayValue (for Table and showing SAQ).
      // Also display -- when displayValue is empty.
      tableRow[columnId === 'average' ? 'userFirst' : columnId] = column.displayValue === '' ? '--' : column.displayValue;
    });
    tableData.push(tableRow);
  });

  // Sort tableData to ensure Average row is at the top by default
  const sortedTableData = [...tableData].sort((rowA, rowB) => {
    const isRowAAverage = rowA.userFirst === 'Average';
    const isRowBAverage = rowB.userFirst === 'Average';
    if (isRowAAverage && !isRowBAverage) return -1; // Average row first
    if (!isRowAAverage && isRowBAverage) return 1; // Average row first
    return 0; // Keep original order for non-Average rows
  });

  // RawValue Average Data Prep
  // Use this array for the chart prep to render team averages.
  const rawTableData = [];
  maxReport?.rows.forEach((row) => {
    const tableRow = {};
    row.forEach((column) => {
      const { columnId } = column;
      // Create a key value based on the columnId and rawValue (for Bar Chart)
      tableRow[columnId === 'average' ? 'userFirst' : columnId] = column.rawValue;
    });
    rawTableData.push(tableRow);
  });

  return (
    <ReportContainer>
      <ChartContainer>
        <MaxChart
          activeAthlete={activeAthlete}
          layoutHeight={layoutHeight}
          setActiveAthlete={setActiveAthlete}
          athletesChartData={athletesChartData}
          setAthletesChartData={setAthletesChartData}
          rawTableData={rawTableData}
        />
      </ChartContainer>
      <TableContainer>
        <MaxTable
          setActiveAthlete={setActiveAthlete}
          tableColumns={tableColumns}
          tableData={sortedTableData}
        />
      </TableContainer>
    </ReportContainer>
  );
};

export default MaxReportData;
